import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { PaginationQueryResponse } from '@vsolv/dev-kit/ngx';
import { Warranty } from '@wsphere/warranties/domain';
import { firstValueFrom, Observable } from 'rxjs';

// Lightweight mode returns a partial warranty, and `retail` isn't on Warranty.Model.
export interface UnfundedWarranty {
  id: string;
  contractNumber: string;
  retail?: number | string;
  // Null when rating didn't price this warranty, which must not render as $0.00.
  netAmountDue?: number | string | null;
  created: string;
  customer?: { id: string; name: string } | null;
  policy?: { id: string; title: string } | null;
  plan?: { id: string; title: string } | null;
}

export interface UnfundedFilterPolicy {
  id: string;
  title: string;
  plans: { id: string; title: string }[];
}

export interface ListUnfundedWarrantiesParams {
  distributorId: string;
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  policyIds?: string[];
  planIds?: string[];
  provisionedStartDate?: Date | null;
  provisionedEndDate?: Date | null;
}

// YYYY-MM-DD in the user's local timezone, matching how the warranty table sends dates.
function formatDate(date: Date | null | undefined): string | undefined {
  return date ? new Date(date).toLocaleDateString('en-CA') : undefined;
}

@Injectable()
export class UnfundedWarrantiesService {
  constructor(private http: HttpClient) {}

  list$({
    distributorId,
    page,
    limit,
    sortBy,
    sortOrder,
    policyIds,
    planIds,
    provisionedStartDate,
    provisionedEndDate,
  }: ListUnfundedWarrantiesParams): Observable<PaginationQueryResponse<UnfundedWarranty>> {
    const start = formatDate(provisionedStartDate);
    const end = formatDate(provisionedEndDate);

    return this.http.get<PaginationQueryResponse<UnfundedWarranty>>('/api/warranties', {
      params: {
        distributorId,
        // PENDING is the display label for DRAFT.
        status: Warranty.Status.DRAFT,
        page: page.toString(),
        limit: limit.toString(),
        includeAllProperties: 'false',
        ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
        ...(policyIds?.length ? { policyIds } : {}),
        ...(planIds?.length ? { planIds } : {}),
        ...(start ? { provisionedStartDate: start } : {}),
        ...(end ? { provisionedEndDate: end } : {}),
      },
    });
  }

  // Policy/plan options for the filters. Same endpoint the warranty table's policy picker uses.
  async listFilterPolicies() {
    return firstValueFrom(
      this.http.get<{ items: UnfundedFilterPolicy[] }>('/api/policies', { params: { page: 1, limit: 100 } })
    );
  }
}
